import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ArrowDownTrayIcon,
  UserIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  KeyIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import ActivateUserModal from '@/components/ActivateUserModal'
import DeactivateUserModal from '@/components/DeactivateUserModal'
import ChangeRoleModal from '@/components/ChangeRoleModal'
import UserAuditModal from '@/components/UserAuditModal'
import api from '@/services/api'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'


interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  fullName: string
  role: string
  isActive: boolean
  lastLogin: string | null
  mustChangePassword: boolean
  createdAt: string
  hasActiveTempCredentials: boolean
  employee?: {
    id: number
    employeeNumber: string
    department: string
    position: string
    employmentStatus: string
  }
}

interface UsersResponse {
  users: User[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

const ROLES = [
  { value: '', label: 'All Roles' },
  { value: 'admin', label: 'Administrator' },
  { value: 'hr_manager', label: 'HR Manager' },
  { value: 'hr_staff', label: 'HR Staff' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employee' }
]

const STATUS_OPTIONS = [
  { value: '', label: 'All Users' },
  { value: 'active', label: 'Active Only' },
  { value: 'inactive', label: 'Inactive Only' }
]

const AdminCredentialsEnhanced: React.FC = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // State management
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set())
  const [showFilters, setShowFilters] = useState(false)

  // Modal states
  const [activateModal, setActivateModal] = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null })
  const [deactivateModal, setDeactivateModal] = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null })
  const [changeRoleModal, setChangeRoleModal] = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null })
  const [auditModal, setAuditModal] = useState<{ isOpen: boolean; user: User | null }>({ isOpen: false, user: null })

  const pageSize = 20
  const isAdmin = user?.role === 'admin' || user?.role === 'hr_manager'

  // Fetch users
  const { data: usersData, isLoading } = useQuery<UsersResponse>({
    queryKey: ['admin-users', currentPage, searchTerm, statusFilter, roleFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString()
      })

      if (searchTerm.trim()) params.append('search', searchTerm.trim())
      if (statusFilter) params.append('status', statusFilter)
      if (roleFilter) params.append('role', roleFilter)

      const response = await api.get(`/admin/users?${params}`)
      return response.data.data as UsersResponse
    },
    enabled: isAdmin,
  })

  // Individual user operations
  const activateUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await api.post(`/admin/users/${userId}/activate`)
      return response.data
    },
    onSuccess: () => {
      toast.success('User activated successfully!')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setActivateModal({ isOpen: false, user: null })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to activate user')
    }
  })

  const deactivateUserMutation = useMutation({
    mutationFn: async ({ userId, reason }: { userId: number; reason?: string }) => {
      const response = await api.post(`/admin/users/${userId}/deactivate`, { reason: reason || '' })
      return response.data
    },
    onSuccess: () => {
      toast.success('User deactivated successfully!')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setDeactivateModal({ isOpen: false, user: null })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to deactivate user')
    }
  })

  const resetPasswordMutation = useMutation({
  mutationFn: async (userId: number) => {
    const response = await api.post(`/admin/users/${userId}/reset-password`)
    return response.data
  },
  onSuccess: (data) => {
    toast.success("Temporary password generated!")

    // Popup pro
    Swal.fire({
  title: "New Temporary Password",
  html: `
    <p><b>Password:</b> ${data.data.tempPassword}</p>
    <p><b>Expires:</b> ${data.data.expiresIn}</p>
  `,
  icon: "success",
  confirmButtonColor: "#4f46e5",
})


    queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  },
  onError: (error: any) => {
    toast.error(error.response?.data?.message || 'Failed to reset password')
  }
})


  const changeRoleMutation = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: number; newRole: string }) => {
      const response = await api.put(`/admin/users/${userId}/role`, { role: newRole })
      return response.data
    },
    onSuccess: () => {
      toast.success('User role updated successfully!')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setChangeRoleModal({ isOpen: false, user: null })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update user role')
    }
  })

  // Bulk operations
  const bulkOperationMutation = useMutation({
    mutationFn: async ({ action, userIds }: { action: string; userIds: number[] }) => {
      const response = await api.post('/admin/users/bulk-operations', { action, userIds })
      return response.data
    },
    onSuccess: (data) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setSelectedUsers(new Set())
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Bulk operation failed')
    }
  })

  // Export users
  const exportUsersMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (roleFilter) params.append('role', roleFilter)

      const response = await api.get(`/admin/users/export?${params}`, {
        responseType: 'blob'
      })

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url

      const timestamp = new Date().toISOString().split('T')[0]
      link.setAttribute('download', `users_export_${timestamp}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    onSuccess: () => {
      toast.success('User data exported successfully!')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to export user data')
    }
  })

  // Event handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
  }

  const handleUserSelect = (userId: number, checked: boolean) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev)
      if (checked) {
        newSet.add(userId)
      } else {
        newSet.delete(userId)
      }
      return newSet
    })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && usersData?.users) {
      setSelectedUsers(new Set(usersData.users.map((u: User) => u.id)))
    } else {
      setSelectedUsers(new Set())
    }
  }

  const handleBulkOperation = (action: string) => {
    if (selectedUsers.size === 0) {
      toast.error('Please select at least one user')
      return
    }

    const actionLabels = {
      activate: 'activate',
      deactivate: 'deactivate',
      force_password_reset: 'force password reset for'
    }

    const actionLabel = actionLabels[action as keyof typeof actionLabels] || action
    const userCount = selectedUsers.size

    if (window.confirm(`Are you sure you want to ${actionLabel} ${userCount} selected user(s)?`)) {
      bulkOperationMutation.mutate({ action, userIds: Array.from(selectedUsers) })
    }
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'hr_manager': return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      case 'hr_staff': return 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30'
      case 'finance_manager': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
      case 'manager': return 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30'
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }

  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Never'
    const date = new Date(lastLogin)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))

    if (diffInHours < 1) return 'Just now'
    if (diffInHours < 24) return `${diffInHours}h ago`
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`
    return date.toLocaleDateString()
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto bg-indigo-600 rounded-full flex items-center justify-center mb-3">
            <ExclamationTriangleIcon className="text-white h-5 w-5" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Access Denied</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            You don't have permission to access user management.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
            <UsersIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">User Management</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Manage user accounts, roles, and access permissions across your organization
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Users</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">
              {usersData?.pagination?.total || 0}
            </div>
          </div>
          <button
            onClick={() => exportUsersMutation.mutate()}
            disabled={exportUsersMutation.isPending}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-3 w-3" />
            {exportUsersMutation.isPending ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or employee number..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-1 px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-600 transition-all duration-200"
            >
              <AdjustmentsHorizontalIcon className="h-3 w-3" />
              Filters
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                  Account Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                >
                  {STATUS_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                  User Role
                </label>
                <select
                  value={roleFilter}
                  onChange={(e) => {
                    setRoleFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                >
                  {ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Operations */}
        {selectedUsers.size > 0 && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-xs font-medium text-blue-900 dark:text-blue-300">
                {selectedUsers.size} user(s) selected
              </span>
              <div className="flex items-center gap-1 flex-wrap">
                <button
                  onClick={() => handleBulkOperation('activate')}
                  disabled={bulkOperationMutation.isPending}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all duration-200 disabled:opacity-50"
                >
                  <CheckCircleIcon className="h-3 w-3" />
                  Activate
                </button>
                <button
                  onClick={() => handleBulkOperation('deactivate')}
                  disabled={bulkOperationMutation.isPending}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-all duration-200 disabled:opacity-50"
                >
                  <XCircleIcon className="h-3 w-3" />
                  Deactivate
                </button>
                <button
                  onClick={() => handleBulkOperation('force_password_reset')}
                  disabled={bulkOperationMutation.isPending}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-all duration-200 disabled:opacity-50"
                >
                  <ClockIcon className="h-3 w-3" />
                  Reset Password
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
            <p className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading users...</p>
          </div>
        ) : !usersData?.users?.length ? (
          <div className="text-center py-8">
            <UserIcon className="h-8 w-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No users found</h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === usersData.users.length && usersData.users.length > 0}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      User
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Role
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Last Login
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {usersData.users.map((user: User) => (
                    <tr
                      key={user.id}
                      className={selectedUsers.has(user.id)
                        ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={(e) => handleUserSelect(user.id, e.target.checked)}
                          className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-600 rounded"
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-lg bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {user.firstName[0]}{user.lastName[0]}
                              </span>
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-xs font-medium text-gray-900 dark:text-white">
                              {user.fullName}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">{user.email}</div>
                            {user.employee && (
                              <div className="text-xs text-gray-500 dark:text-gray-500">
                                {user.employee.employeeNumber} • {user.employee.department}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getRoleColor(user.role)}`}>
                          {ROLES.find(r => r.value === user.role)?.label || user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center">
                            {user.isActive ? (
                              <CheckCircleIcon className="h-3 w-3 text-emerald-500 mr-1" />
                            ) : (
                              <XCircleIcon className="h-3 w-3 text-red-500 mr-1" />
                            )}
                            <span className={user.isActive
                              ? 'text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 rounded'
                              : 'text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded'
                            }>
                              {user.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          {user.hasActiveTempCredentials && (
                            <div className="flex items-center">
                              <ClockIcon className="h-3 w-3 text-amber-500 mr-1" />
                              <span className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded">Temp Credentials</span>
                            </div>
                          )}
                          {user.mustChangePassword && (
                            <div className="text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 font-medium px-1.5 py-0.5 rounded inline-block">Must Change Password</div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-medium">
                        {formatLastLogin(user.lastLogin)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setAuditModal({ isOpen: true, user })}
                            className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="View Activity"
                          >
                            <EyeIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => setChangeRoleModal({ isOpen: true, user })}
                            className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="Change Role"
                          >
                            <Cog6ToothIcon className="h-3 w-3" />
                          </button>
                          <button
                              onClick={() => resetPasswordMutation.mutate(user.id)}
                              className="text-gray-400 hover:text-amber-500 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Reset Password"
                            >
                              <KeyIcon className="h-3 w-3" />
                            </button>

                          {user.isActive ? (
                            <button
                              onClick={() => setDeactivateModal({ isOpen: true, user })}
                              className="text-gray-400 hover:text-red-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Deactivate"
                            >
                              <XCircleIcon className="h-3 w-3" />
                            </button>
                          ) : (
                            <button
                              onClick={() => setActivateModal({ isOpen: true, user })}
                              className="text-gray-400 hover:text-emerald-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                              title="Activate"
                            >
                              <CheckCircleIcon className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {usersData?.pagination && usersData.pagination.pages > 1 && (
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Showing {((usersData.pagination.page - 1) * usersData.pagination.limit) + 1} to{' '}
                    {Math.min(usersData.pagination.page * usersData.pagination.limit, usersData.pagination.total)} of{' '}
                    {usersData.pagination.total} results
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage <= 1}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                      Page {usersData.pagination.page} of {usersData.pagination.pages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, usersData.pagination.pages))}
                      disabled={currentPage >= usersData.pagination.pages}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <ActivateUserModal
        isOpen={activateModal.isOpen}
        onClose={() => setActivateModal({ isOpen: false, user: null })}
        employee={{
          id: activateModal.user?.id || 0,
          employeeNumber: activateModal.user?.employee?.employeeNumber || '',
          firstName: activateModal.user?.firstName || '',
          lastName: activateModal.user?.lastName || '',
          email: activateModal.user?.email || '',
          employmentStatus: activateModal.user?.employee?.employmentStatus || 'inactive'
        }}
        onConfirm={async (userId: number) => activateUserMutation.mutateAsync(userId)}
        isLoading={activateUserMutation.isPending}
      />

      <DeactivateUserModal
        isOpen={deactivateModal.isOpen}
        onClose={() => setDeactivateModal({ isOpen: false, user: null })}
        user={deactivateModal.user}
        onConfirm={async (userId: number, reason?: string) => deactivateUserMutation.mutateAsync({ userId, reason: reason || '' })}
        isLoading={deactivateUserMutation.isPending}
      />

      <ChangeRoleModal
        isOpen={changeRoleModal.isOpen}
        onClose={() => setChangeRoleModal({ isOpen: false, user: null })}
        user={changeRoleModal.user}
        onConfirm={async (userId: number, newRole: string) => changeRoleMutation.mutateAsync({ userId, newRole })}
        isLoading={changeRoleMutation.isPending}
      />

      <UserAuditModal
        isOpen={auditModal.isOpen}
        onClose={() => setAuditModal({ isOpen: false, user: null })}
        user={auditModal.user}
      />
    </div>
  )
}

export default AdminCredentialsEnhanced